import type { AnalysisOptions, VariableMapping } from '../stores/systems/keisuke/dashboardStore';

const ANALYSIS_TYPE_KEYS = [
  't-test',
  'anova',
  'dose-response',
  'survival',
  'basic-stats',
  'qpcr',
  'longitudinal',
  'regression',
  'bar-chart',
] as const;

type AnalysisTypeKey = (typeof ANALYSIS_TYPE_KEYS)[number];

function normalizeAnalysisType(raw: unknown): AnalysisTypeKey {
  if (typeof raw === 'string' && (ANALYSIS_TYPE_KEYS as readonly string[]).includes(raw)) {
    return raw as AnalysisTypeKey;
  }
  return 't-test';
}

const MAPPING_KEYS = [
  'groupColumn',
  'valueColumn',
  'doseColumn',
  'effectColumn',
  'totalColumn',
  'factorAColumn',
  'factorBColumn',
  'subjectColumn',
  'beforeColumn',
  'timeColumn',
  'eventColumn',
  'survivalGroupColumn',
] as const satisfies readonly (keyof VariableMapping)[];

const PAPER_TEXT_LOCALES = new Set(['en', 'ja', 'zh-CN', 'fr', 'es', 'de', 'pt', 'ru']);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function sanitizeString(value: unknown, maxLen: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().slice(0, maxLen);
  return trimmed || undefined;
}

function sanitizeVariableMapping(raw: unknown): VariableMapping | undefined {
  if (!isPlainObject(raw)) return undefined;
  const mapping: VariableMapping = {};
  for (const key of MAPPING_KEYS) {
    const v = sanitizeString(raw[key], 120);
    if (v) mapping[key] = v;
  }
  return Object.keys(mapping).length > 0 ? mapping : undefined;
}

function sanitizeCalibrationUnknownSamples(raw: unknown): AnalysisOptions['calibrationUnknownSamples'] {
  if (!Array.isArray(raw)) return undefined;
  const out: NonNullable<AnalysisOptions['calibrationUnknownSamples']> = [];
  for (const item of raw.slice(0, 200)) {
    if (!isPlainObject(item)) continue;
    const name = sanitizeString(item.name, 120);
    const absorbance = typeof item.absorbance === 'number' && Number.isFinite(item.absorbance)
      ? item.absorbance
      : Number.parseFloat(String(item.absorbance ?? ''));
    if (!name || !Number.isFinite(absorbance)) continue;
    out.push({ name, absorbance });
  }
  return out.length > 0 ? out : undefined;
}

/** 信頼できない .keisuke 由来の analysisOptions を既知キーのみに制限する */
export function sanitizeAnalysisOptions(
  raw: unknown,
  fallback: AnalysisOptions = { type: 't-test', mapping: {} }
): AnalysisOptions {
  if (!isPlainObject(raw)) return { ...fallback };

  const out: AnalysisOptions = { ...fallback };

  if (raw.type !== undefined && raw.type !== '') {
    out.type = normalizeAnalysisType(raw.type);
  }
  if (raw.testType === 'unpaired' || raw.testType === 'paired') out.testType = raw.testType;
  if (raw.ttestInferential === 'parametric' || raw.ttestInferential === 'mann-whitney' || raw.ttestInferential === 'wilcoxon') {
    out.ttestInferential = raw.ttestInferential;
  }
  if (typeof raw.equalVariance === 'boolean') out.equalVariance = raw.equalVariance;
  if (raw.anovaType === 'oneWay' || raw.anovaType === 'twoWay' || raw.anovaType === 'repeatedMeasures') {
    out.anovaType = raw.anovaType;
  }
  if (raw.anovaRepeatedMethod === 'repeated' || raw.anovaRepeatedMethod === 'lmm') {
    out.anovaRepeatedMethod = raw.anovaRepeatedMethod;
  }
  if (raw.anovaOneWayMethod === 'anova' || raw.anovaOneWayMethod === 'kruskal-wallis') {
    out.anovaOneWayMethod = raw.anovaOneWayMethod;
  }
  if (raw.twoWayMethod === 'independent' || raw.twoWayMethod === 'mixed' || raw.twoWayMethod === 'lmm' || raw.twoWayMethod === 'full-repeated') {
    out.twoWayMethod = raw.twoWayMethod;
  }
  if (typeof raw.twoWayFullRepeatedConfirm === 'boolean') out.twoWayFullRepeatedConfirm = raw.twoWayFullRepeatedConfirm;
  if (typeof raw.postHoc === 'string') out.postHoc = raw.postHoc.slice(0, 40);
  if (typeof raw.anovaControlGroup === 'string') out.anovaControlGroup = raw.anovaControlGroup.slice(0, 120);
  if (typeof raw.ttestComparisonCount === 'number' && Number.isFinite(raw.ttestComparisonCount)) {
    out.ttestComparisonCount = Math.max(1, Math.min(999, Math.floor(raw.ttestComparisonCount)));
  }
  if (typeof raw.paperTextLocale === 'string' && PAPER_TEXT_LOCALES.has(raw.paperTextLocale)) {
    out.paperTextLocale = raw.paperTextLocale as AnalysisOptions['paperTextLocale'];
  }
  if (raw.drMethod === 'probit' || raw.drMethod === 'logistic' || raw.drMethod === 'reed-muench' || raw.drMethod === 'spearman-karber') {
    out.drMethod = raw.drMethod;
  }
  if (raw.drType === 'ld50' || raw.drType === 'ec50' || raw.drType === 'ec90' || raw.drType === 'tcid50') {
    out.drType = raw.drType;
  }
  if (raw.qpcrMethod === 'deltaCt' || raw.qpcrMethod === 'deltaDeltaCt') out.qpcrMethod = raw.qpcrMethod;
  if (raw.regressionType === 'standard' || raw.regressionType === 'origin') out.regressionType = raw.regressionType;
  if (typeof raw.regressionTypeManual === 'boolean') out.regressionTypeManual = raw.regressionTypeManual;
  if (raw.regressionMode === 'scatter' || raw.regressionMode === 'calibration') out.regressionMode = raw.regressionMode;

  const mapping = sanitizeVariableMapping(raw.mapping);
  if (mapping) out.mapping = { ...fallback.mapping, ...mapping };

  const unknownSamples = sanitizeCalibrationUnknownSamples(raw.calibrationUnknownSamples);
  if (unknownSamples) out.calibrationUnknownSamples = unknownSamples;

  return out;
}
